import React from 'react';
import Button from '../../lib/button/Button';
import DisplayDataStructure from '../../lib/display-data-structure';
import {
	ChangeSet,
	createDefinitionEntityFromJSONObject,
	DefinitionEntityState,
	EntityType,
	findEntityFromKeys,
	getParentCanvasEntity,
	LiteralValueState,
	PrimitiveTypes,
	ProjectState,
	EntityWithValueState,
	resolveEntityDataTypeLabel,
	resolveEntityName,
	READABLE_ENTITY_TYPES,
	TERMINATION_TYPES,
	lowercaseFirstLetter,
	isObject,
	StateMutationAction
} from '@elyx-code/project-logic-tree';
import { Canvas, DraggableObject } from '../../lib/canvas';
import {
	calculateCardSize,
	CANVAS_CARD_WIDTH,
	CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS,
	getCanvasEntityDraggableContainerDOMId
} from '../../services/editor/ui';
import { DynamicValueTypes } from '../../services/editor/value-store';
import { EditorService } from '../../services/editor';
import { popupNotification } from '../../popup-notification';
import { entitiesToUniqueCanvasEntities } from '../../services/editor/utils';

interface IComplexDataDialogProps {
	onClose: (event: React.MouseEvent) => void;
	valueOwner: EntityWithValueState;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	canvas: Canvas;
	draggableObject: DraggableObject;
	project: EditorService;
}

const ComplexDataDialog: React.FC<IComplexDataDialogProps> = (props) => {
	// Based on the entity type, we resolve the header text
	const resolveHeaderText = () => {
		return resolveEntityName(props.valueOwner, props.valueOwner.project);
	};

	const valueOwnerCanvasParentEntity = getParentCanvasEntity(
		props.valueOwner
	);

	const resolveSubHeaderText = () => {
		let entityTypeDescription = '';

		const parentEntityName = resolveEntityName(
			valueOwnerCanvasParentEntity,
			props.valueOwner.project
		);

		const parentEntityDescriptor =
			props.valueOwner.id !== valueOwnerCanvasParentEntity.id
				? ' of ' +
					parentEntityName +
					' ' +
					lowercaseFirstLetter(
						READABLE_ENTITY_TYPES[valueOwnerCanvasParentEntity.type]
							.singular
					)
				: '';

		switch (props.valueOwner.type) {
			case EntityType.VariableDeclaration:
				entityTypeDescription = 'Variable';
				break;
			case EntityType.ArgumentDeclaration:
				entityTypeDescription = 'Input';
				break;
			case EntityType.InputMap: {
				entityTypeDescription = 'Input';

				if (
					TERMINATION_TYPES.includes(
						valueOwnerCanvasParentEntity.type
					)
				) {
					entityTypeDescription = 'Output';
				}
				break;
			}
			case EntityType.OutputMap:
				entityTypeDescription = 'Output';
				break;
		}

		return entityTypeDescription + parentEntityDescriptor;
	};

	const valueOwnerDataType = props.valueOwner.getDataType(null);

	const hasStrictObjectStructure =
		valueOwnerDataType?.hasStrictObjectStructure();
	const isObjectStructure = valueOwnerDataType?.isObject();

	const hasInlineDeclaredObjectDataTypeEntity =
		!!valueOwnerDataType?.entity &&
		valueOwnerDataType?.entity.type === EntityType.DefinitionEntity &&
		valueOwnerDataType?.entity.parent?.id === valueOwnerDataType?.id;

	const canInlineDeclaredObjectDataTypeEntityBeExplicit =
		hasInlineDeclaredObjectDataTypeEntity &&
		![EntityType.Search, EntityType.Loop].includes(
			props.valueOwner.parent?.type
		);

	const isUntyped =
		!valueOwnerDataType ||
		!valueOwnerDataType?.entity ||
		valueOwnerDataType?.entity.name === PrimitiveTypes.Untyped;

	const keys =
		!!props.value?.value &&
		isObject(props.value?.value) &&
		Object.keys(props.value?.value);

	const existingGloballyDeclaredDefinitionEntity =
		!!props.value?.value &&
		isObject(props.value?.value) &&
		findEntityFromKeys(keys, props.project.logic);

	const showGoToDefinitionButton = !!existingGloballyDeclaredDefinitionEntity;

	const showMakeDefinitionExplicitButton =
		!existingGloballyDeclaredDefinitionEntity &&
		canInlineDeclaredObjectDataTypeEntityBeExplicit &&
		hasInlineDeclaredObjectDataTypeEntity;

	const showCreateDefinitionEntityButton =
		!showMakeDefinitionExplicitButton &&
		(!valueOwnerDataType ||
			(isObjectStructure && !hasStrictObjectStructure) ||
			(isUntyped && isObject(props.value?.value)));

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				padding: '40px'
			}}
		>
			{/* Header */}
			<span>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						className="dialog_back_button"
						onClick={(e) => {
							props.onClose?.(e);
						}}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px'
						}}
					>
						{resolveHeaderText()}
					</h1>
				</div>
				<span
					style={{ display: 'flex', gap: '6px', marginLeft: '42px' }}
				>
					<p style={{ margin: '0px', color: 'rgba(0,0,0,0.7)' }}>
						{resolveSubHeaderText()}
					</p>
				</span>
			</span>

			<div
				style={{
					flex: 1,
					margin: '16px 42px',
					marginTop: '16px',
					overflow: 'scroll'
				}}
			>
				<section>
					<span
						style={{
							display: 'flex',
							gap: '10px',
							alignItems: 'center'
						}}
					>
						<h3
							style={{
								display: 'flex',
								gap: '6px',
								fontWeight: 'normal',
								margin: '0px'
							}}
						>
							Value
						</h3>
					</span>
					<p
						style={{
							margin: '0px',
							fontSize: '14px',
							marginTop: '6px',
							color: 'rgba(0,0,0,0.54)',
							marginBottom: '16px'
						}}
					>
						{resolveEntityDataTypeLabel(props.valueOwner)}
					</p>
					<DisplayDataStructure
						value={
							props.value?.name === PrimitiveTypes.String
								? props.value?.rawString || props.value?.value
								: props.value?.value
						}
					/>
				</section>
			</div>

			<section
				style={{
					alignSelf: 'flex-start',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-end',
					width: '100%',
					gap: '16px'
				}}
			>
				<Button
					onClick={(e: any) => {
						props.onClose?.(e);
					}}
					variant="text"
				>
					Back
				</Button>
				{showCreateDefinitionEntityButton && (
					<Button
						onClick={async () => {
							const changeSet = props.project.logic.addChangeSet(
								new ChangeSet(
									props.project.logic,
									ProjectState.sessionAuthor,
									new Date().toISOString(),
									props.project.logic,
									false,
									StateMutationAction.CreateDefFromValue
								)
							);

							const result = createDefinitionEntityFromJSONObject(
								props.value?.value as {
									[key: string]: any;
								},
								props.project.logic,
								{
									name:
										resolveEntityName(
											props.valueOwner,
											props.project.logic
										) + ' definition'
								},
								changeSet
							);

							const resolvedDefinitionEntity =
								result.changeSet.self;

							if (result.hasCreated) {
								let newX = valueOwnerCanvasParentEntity.x;
								const newY =
									valueOwnerCanvasParentEntity.y -
									calculateCardSize(resolvedDefinitionEntity)
										.height -
									CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS;

								const newCanvasEntities =
									entitiesToUniqueCanvasEntities(
										result.changeSet.added
									);

								// Give them a valid x and y coordinates
								newCanvasEntities.forEach((entity) => {
									newX += CANVAS_CARD_WIDTH * 2;

									// @ts-ignore
									entity.metaSync(
										{
											x: newX,
											y: newY
										},
										changeSet
									);
								});

								const objectId =
									getCanvasEntityDraggableContainerDOMId(
										resolvedDefinitionEntity
									);

								props.project.canvas?.scrollToObjectById(
									objectId,
									() => {
										props.onClose?.(null);
									}
								);
							} else {
								// Create notification that the entity already exists
								// Then scroll to the entity
								popupNotification({
									text: 'Compatible definition already exists',
									color: 'warning'
								});

								const canvasEntity = getParentCanvasEntity(
									resolvedDefinitionEntity
								);

								const objectId =
									getCanvasEntityDraggableContainerDOMId(
										canvasEntity
									);

								props.project.canvas?.scrollToObjectById(
									objectId,
									() => {
										props.onClose?.(null);
									}
								);
							}

							props.project.renderAndCloseChangeSet(changeSet);
						}}
					>
						Create definition
					</Button>
				)}
				{showMakeDefinitionExplicitButton && (
					<Button
						onClick={async () => {
							const changeSet = props.project.logic.addChangeSet(
								new ChangeSet(
									props.project.logic,
									ProjectState.sessionAuthor,
									new Date().toISOString(),
									props.project.logic,
									false,
									StateMutationAction.MoveInlineDefToCanvas
								)
							);

							const existingInlineEntity =
								valueOwnerDataType?.entity as DefinitionEntityState;

							existingInlineEntity.setParent(
								props.project.logic,
								changeSet
							);
							valueOwnerDataType.metaSync(
								{
									inferred: false
								},
								changeSet
							);

							let newX = valueOwnerCanvasParentEntity.x;
							const newY =
								valueOwnerCanvasParentEntity.y -
								calculateCardSize(existingInlineEntity).height -
								CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS;

							const newCanvasEntities =
								entitiesToUniqueCanvasEntities([
									existingInlineEntity
								]);

							// Give them a valid x and y coordinates
							newCanvasEntities.forEach((entity) => {
								newX += CANVAS_CARD_WIDTH * 2;

								// @ts-ignore
								entity.metaSync(
									{
										x: newX,
										y: newY
									},
									changeSet
								);
							});

							const objectId =
								getCanvasEntityDraggableContainerDOMId(
									existingInlineEntity
								);

							props.project.canvas?.scrollToObjectById(
								objectId,
								() => {
									props.onClose?.(null);
								}
							);

							props.project.renderAndCloseChangeSet(changeSet);
						}}
					>
						Create definition
					</Button>
				)}
				{showGoToDefinitionButton && (
					<Button
						onClick={(e: any) => {
							const canvasEntity = getParentCanvasEntity(
								valueOwnerDataType.entity
							);

							const objectId =
								getCanvasEntityDraggableContainerDOMId(
									canvasEntity
								);

							props.project.canvas?.scrollToObjectById(
								objectId,
								() => {
									props.onClose?.(e);
								}
							);
						}}
					>
						Go to definition
					</Button>
				)}
			</section>
		</div>
	);
};

export default ComplexDataDialog;
